import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTypedSelector } from '../../reducers'
import { loadBannerStatusMessages } from '../../actions/systemStatus'
import ISystemStatusMessage, {
  Severity
} from '../../model/ISystemStatusMessage'
import { getActiveMessages, messageContentHash } from '../../utils/systemStatus'

// How often to re-fetch the banner feed and re-derive active messages so that
// scheduled boundaries (Pending→Active→Expired) take effect without a reload.
const POLL_MS = 60_000
const TICK_MS = 30_000

const DISMISS_PREFIX = 'mca.statusBanner.dismissed.'
const keyFor = (id: number) => `${DISMISS_PREFIX}${id}`

const readDismissed = (id: number): string | null => {
  try {
    return window.localStorage.getItem(keyFor(id))
  } catch {
    return null
  }
}

const writeDismissed = (id: number, hash: string) => {
  try {
    window.localStorage.setItem(keyFor(id), hash)
  } catch {
    // ignore storage failures — dismissal is best-effort
  }
}

const clearDismissed = (id: number) => {
  try {
    window.localStorage.removeItem(keyFor(id))
  } catch {
    // ignore
  }
}

export interface SystemStatusBannerState {
  visibleMessages: ISystemStatusMessage[]
  activeCount: number
  // number of active messages currently hidden (dismissed by the user)
  hiddenCount: number
  highestSeverity: Severity | null
  // true when at least one active message is currently hidden (dismissed)
  hasHiddenMessages: boolean
  dismiss: (id: number) => void
  restoreAll: () => void
}

export const useSystemStatusBanner = (): SystemStatusBannerState => {
  const dispatch = useDispatch()
  const messages = useTypedSelector(state => state.systemStatus.bannerMessages)
  const [now, setNow] = useState(() => Date.now())
  // bumped whenever dismissal state changes, to force a recompute
  const [dismissVersion, setDismissVersion] = useState(0)

  // Fetch on mount, then poll.
  useEffect(() => {
    dispatch(loadBannerStatusMessages())
    const poll = window.setInterval(
      () => dispatch(loadBannerStatusMessages()),
      POLL_MS
    )
    return () => window.clearInterval(poll)
  }, [dispatch])

  // Re-derive active set on a lighter cadence than the network poll.
  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), TICK_MS)
    return () => window.clearInterval(tick)
  }, [])

  // Refresh `now` whenever the message set changes (e.g. right after creating a
  // message), so a just-created message with startDate ≈ now isn't briefly
  // classified as Pending against a stale `now` and hidden until the next tick.
  useEffect(() => {
    setNow(Date.now())
  }, [messages])

  const activeMessages = useMemo(() => getActiveMessages(messages, now), [
    messages,
    now
  ])

  // Prune dismissal keys for messages that are no longer active. Iterate
  // backwards: clearDismissed removes entries as we go, which shifts the
  // indexes of everything after it — a forward loop would skip the next key.
  // The whole pass is wrapped so a throwing localStorage keeps cleanup
  // best-effort, matching the read/write helpers.
  useEffect(() => {
    // Skip while the feed hasn't loaded (or is momentarily empty): with no
    // messages every dismissal key would look stale and we'd wipe them all on
    // startup, un-dismissing everything the user had already dismissed.
    if (messages.length === 0) {
      return
    }
    const activeIds = new Set(activeMessages.map(m => m.id))
    try {
      for (let i = window.localStorage.length - 1; i >= 0; i--) {
        const key = window.localStorage.key(i)
        if (key?.startsWith(DISMISS_PREFIX)) {
          const id = Number(key.slice(DISMISS_PREFIX.length))
          if (!activeIds.has(id)) {
            clearDismissed(id)
          }
        }
      }
    } catch {
      // ignore — dismissal cleanup is best-effort
    }
  }, [activeMessages, messages.length])

  const visibleMessages = useMemo(() => {
    // referenced so the memo re-runs after a dismiss/restore
    void dismissVersion
    return activeMessages.filter(msg => {
      const dismissedHash = readDismissed(msg.id)
      // Visible unless dismissed AND the content is unchanged since dismissal.
      return dismissedHash !== messageContentHash(msg)
    })
  }, [activeMessages, dismissVersion])

  const dismiss = useCallback(
    (id: number) => {
      const msg = activeMessages.find(m => m.id === id)
      if (msg) {
        writeDismissed(id, messageContentHash(msg))
        setDismissVersion(v => v + 1)
      }
    },
    [activeMessages]
  )

  const restoreAll = useCallback(() => {
    activeMessages.forEach(m => clearDismissed(m.id))
    setDismissVersion(v => v + 1)
  }, [activeMessages])

  const highestSeverity =
    activeMessages.length > 0 ? activeMessages[0].type : null

  return {
    visibleMessages,
    activeCount: activeMessages.length,
    hiddenCount: activeMessages.length - visibleMessages.length,
    highestSeverity,
    hasHiddenMessages:
      activeMessages.length > 0 &&
      visibleMessages.length < activeMessages.length,
    dismiss,
    restoreAll
  }
}
